import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Clear as ClearIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { Modal } from '../../../common/components/Modal';
import { MarkdownAttachment, MarkdownService } from '../services/markdownService';

export const GLOBAL_MODULE_ID = 'Global';

const DATA_TEST_ID_SUGGESTIONS = ['help-document-download'];

interface HelpContent {
  id: string;
  moduleId: string;
  title: string;
  content: string;
  contentType: 'markdown' | 'video';
  createdAt: string;
  updatedAt: string;
  dataTestId?: string;
  pageUrl?: string;
  attachmentList?: MarkdownAttachment[];
}

interface HelpGlobalEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pass an existing entry to edit, or null to create a new one */
  content: HelpContent | null;
  onSave: (content: HelpContent & { attachmentList: MarkdownAttachment[] }) => void;
}

const HelpGlobalEditModal: React.FC<HelpGlobalEditModalProps> = ({
  isOpen,
  onClose,
  content,
  onSave,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dataTestId, setDataTestId] = useState('');
  const [attachment, setAttachment] = useState<{ name: string; url: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Populate fields when editing an existing entry
  useEffect(() => {
    if (content) {
      setDataTestId(content.dataTestId || '');
      // Prefer attachmentList; fall back to parsing the content markdown link
      if (content.attachmentList && content.attachmentList.length > 0) {
        const first = content.attachmentList[0];
        setAttachment({ name: first.filename, url: first.url });
      } else {
        // Try to parse [filename](url) from content
        const match = content.content?.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (match) {
          setAttachment({ name: match[1], url: match[2] });
        } else {
          setAttachment(null);
        }
      }
    } else {
      setDataTestId('');
      setAttachment(null);
    }
    setUploadError(null);
  }, [content, isOpen]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setIsUploading(true);
    setUploadError(null);
    try {
      const url = await MarkdownService.uploadFile(file, 'attachment');
      setAttachment({ name: file.name, url });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t('help.uploadError', 'Upload failed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setAttachment(null);
  };

  const handleSave = () => {
    if (!dataTestId || !attachment) return;

    const attachmentList: MarkdownAttachment[] = [
      { url: attachment.url, filename: attachment.name, included: true },
    ];

    onSave({
      id: content?.id || Date.now().toString(),
      moduleId: GLOBAL_MODULE_ID,
      title: attachment.name,
      content: `[${attachment.name}](${attachment.url})`,
      contentType: 'markdown',
      createdAt: content?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dataTestId,
      pageUrl: '/global',
      attachmentList,
    });
  };

  const isValid = !!dataTestId.trim() && !!attachment && !isUploading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        content
          ? t('help.editGlobalAttachment', 'Edit attachment')
          : t('help.addGlobalAttachment', 'Add attachment')
      }
      maxWidth="sm"
      onApply={handleSave}
      applyText={t('common.save', 'Save')}
      cancelText={t('common.cancel', 'Cancel')}
      disableSave={!isValid}
      data-testid="help-global-edit-modal"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
        {/* Data Test ID */}
        <Autocomplete
          freeSolo
          options={DATA_TEST_ID_SUGGESTIONS}
          value={dataTestId}
          onInputChange={(_e, value) => setDataTestId(value)}
          data-testid="help-global-testid-field"
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('help.dataTestId', 'Data Test ID')}
              required
              fullWidth
              helperText={t(
                'help.dataTestIdGlobalHelper',
                'The data-testid of the component where clicking will trigger the download.'
              )}
            />
          )}
        />

        {/* File upload area */}
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('help.attachmentFile', 'Attachment file')} *
          </Typography>

          {/* Hidden input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.docx,.doc,.csv,.zip,.pptx,.ppt,.txt"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {attachment ? (
            /* Show current file */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<AttachFileIcon />}
                label={attachment.name}
                variant="outlined"
                color="primary"
                onDelete={handleRemoveFile}
                deleteIcon={
                  <Tooltip title={t('help.removeAttachment', 'Remove file')}>
                    <ClearIcon />
                  </Tooltip>
                }
              />
              <Tooltip title={t('help.replaceAttachment', 'Replace with a different file')}>
                <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                  <UploadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            /* Upload button */
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              data-testid="help-global-upload-button"
            >
              {t('help.uploadAttachment', 'Upload Attachment (PDF, Word, Excel, …)')}
            </Button>
          )}

          {isUploading && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                {t('help.uploading', 'Uploading…')}
              </Typography>
            </Box>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {uploadError}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t(
              'help.uploadAttachmentInstructions',
              'Supported: PDF, Excel, Word, CSV, ZIP. File is uploaded and a download link is embedded in the markdown.'
            )}
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

export default HelpGlobalEditModal;


